import { Link } from "react-router-dom";
import { ArrowRight, HandHeart, Heart, Handshake, Users, MapPin } from "lucide-react";
import { FadeIn } from "./motion";
import { StageBadge, StageAction, stageOf } from "./ProjectStage";
import { EvidenceBadges } from "./EvidenceBadges";

export const PRINCIPLE = [
  "Listen first.", "Validate with the community.", "Choose projects by evidence.", "Fund transparently.",
  "Implement with local ownership.", "Maintain with community responsibility.", "Report publicly.", "Repeat with improvement.",
];
export const PROCESS = ["Listen", "Verify", "Design", "Fund", "Implement", "Monitor", "Report", "Improve"];
const PROCESS_TEXT = ["Community research with consent — people define the need.", "Field verification of every reported need before any commitment.", "Co-design the intervention with engineers and the community.", "Transparent funding with a public project number and progress bar.", "Local ownership: community members build alongside our teams.", "Follow-up visits check the intervention still works and serves.", "Photos, budgets and outcomes published for donors and community.", "Lessons learned feed the next cycle — repeat with improvement."];

export const AREAS = [
  { t: "Emergency & Basic Needs", d: "Food, emergency relief and immediate support.", to: "/emergency", img: "https://images.unsplash.com/photo-1586900632557-b99cf3b226cd?w=900&q=80" },
  { t: "Education & Skills", d: "Education support, scholarships and training.", to: "/programmes/education", img: "https://images.unsplash.com/photo-1617056239820-8ce90ba48193?w=900&q=80" },
  { t: "Empowerment & Livelihoods", d: "Women, youth, business, agriculture and skills.", to: "/empowerment", img: "https://images.unsplash.com/photo-1509099381441-ea3c0cf98b94?w=900&q=80" },
  { t: "Shelter & Housing", d: "Emergency shelter, repairs, rebuilding and housing.", to: "/housing", img: "https://images.unsplash.com/photo-1565958923272-e96dbb1e8414?w=900&q=80" },
  { t: "Health & Wellbeing", d: "Community health, wellbeing and verified medical support.", to: "/programmes/health", img: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=900&q=80" },
  { t: "Community Infrastructure", d: "Energy, water, sanitation and community assets.", to: "/programmes/light-up-rural-nigeria", img: "https://images.unsplash.com/photo-1776090416729-20bedcc1a207?w=900&q=80" },
];

const JOURNEYS = [
  { icon: HandHeart, t: "I Need Help", d: "Apply for support for yourself, your family or your community. Applying is always free.", cta: "Get help", to: "/get-help" },
  { icon: Heart, t: "I Want to Give", d: "Give to the General Fund, a verified project or the Emergency Fund — every naira traceable.", cta: "Donate", to: "/donate" },
  { icon: Users, t: "I Want to Volunteer", d: "Share your time and skills. Every approved volunteer receives a permanent DKF-VOL number.", cta: "Volunteer", to: "/volunteer" },
  { icon: Handshake, t: "I Want to Partner", d: "Work with us as an organisation, business, university, NGO, church or development agency.", cta: "Partner with us", to: "/partner/apply" },
];

export const QuickActions = () => (
  <section className="py-10 md:py-14 bg-white" data-testid="quick-actions" aria-label="Choose your journey">
    <div className="dk-container grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {JOURNEYS.map((a, i) => (
        <FadeIn key={a.t} delay={i * 0.08}>
          <Link to={a.to} className="group block h-full p-7 rounded-2xl bg-[#f8f4e8] border border-[#d6aa43]/15 hover:border-[#d6aa43]/60 hover:-translate-y-1 transition-all" data-testid={`quick-action-${i}`}>
            <div className="w-12 h-12 rounded-full bg-[#155e3f] text-[#d6aa43] flex items-center justify-center mb-5"><a.icon size={22} /></div>
            <h2 className="font-serif text-2xl text-[#0a0d12] font-bold tracking-tight">{a.t}</h2>
            <p className="text-sm text-[#0a0d12]/70 mt-2 leading-relaxed min-h-[60px]">{a.d}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0d1724] group-hover:text-[#d6aa43]">{a.cta} <ArrowRight size={14} /></span>
          </Link>
        </FadeIn>
      ))}
    </div>
  </section>
);

export const HowWeWork = () => (
  <section className="py-20 md:py-24 bg-[#155e3f] text-white relative overflow-hidden" data-testid="how-we-work">
    <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#d6aa43]/10 blur-3xl pointer-events-none" />
    <div className="dk-container relative grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">How DKF works</span>
        <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3">Verified needs. Traceable projects. Public reporting.</h2>
        <p className="text-white mt-5 leading-relaxed">No project is funded before the need is verified in the field and the budget approved. Every project carries a permanent DKF-PRJ number, and spend, photographs and follow-up results are published as they happen.</p>
        <Link to="/operating-model" className="dk-btn-outline mt-8" data-testid="how-we-work-cta">See the full operating model <ArrowRight size={16} /></Link>
      </div>
      <ol className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="process-steps">
        {PROCESS.map((s, i) => (
          <li key={s} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="font-mono text-[10px] text-[#d6aa43]">STEP {i + 1}</div>
            <div className="font-serif text-lg md:text-xl font-bold mt-1">{s}</div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const ProcessGrid = () => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="process-grid">
    {PROCESS.map((s, i) => (
      <FadeIn key={s} delay={i * 0.06}>
        <div className="rounded-xl border border-black/10 bg-white h-full overflow-hidden group">
          <div className="relative aspect-[3/2] overflow-hidden">
            <img src={`/process/step-${i + 1}.jpg`} alt={`${s} — step ${i + 1} of how we work`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-testid={`process-step-image-${i + 1}`} />
            <div className="absolute top-2 left-3 text-[10px] font-mono text-[#d6aa43] bg-[#155e3f]/80 px-2 py-0.5 rounded">STEP {i + 1}</div>
          </div>
          <div className="p-4"><div className="font-serif text-xl font-bold uppercase tracking-tight text-[#0a0d12]">{s}</div><p className="text-xs text-[#0a0d12]/70 mt-2 leading-relaxed">{PROCESS_TEXT[i]}</p></div>
        </div>
      </FadeIn>
    ))}
  </div>
);

export const ProgrammeAreas = () => (
  <section className="py-20 md:py-24 bg-[#f8f4e8]" data-testid="programme-areas">
    <div className="dk-container">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div className="max-w-2xl"><span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">What we do</span><h2 className="font-serif text-[#0a0d12] text-3xl md:text-5xl font-bold mt-3">Six areas of work. One connected system.</h2></div>
        <Link to="/programmes" className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]">All programmes <ArrowRight size={16} /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {AREAS.map((a, i) => (
          <Link key={a.t} to={a.to} className="group relative rounded-2xl overflow-hidden h-64 block" data-testid={`area-${i}`}>
            <img src={a.img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#155e3f] via-[#155e3f]/55 to-transparent" />
            <div className="absolute bottom-0 p-6 text-white"><h3 className="font-serif text-2xl font-bold">{a.t}</h3><p className="text-sm text-white mt-1">{a.d}</p></div>
            <span className="absolute top-3 right-3 text-[9px] uppercase tracking-wider text-white/90 bg-black/40 px-2 py-0.5 rounded">Illustrative image</span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const UrgentProjectCard = ({ p }) => {
  const stage = stageOf(p);
  const target = p.target_amount_numeric || 0;
  const raised = p.raised_amount || 0;
  const pct = target ? Math.min(100, Math.round((raised / target) * 100)) : p.progress_percent || 0;
  const gap = target ? Math.max(0, target - raised) : null;
  return (
    <article className="bg-white rounded-2xl overflow-hidden border border-black/5 hover:shadow-xl transition-shadow flex flex-col" data-testid={`urgent-project-${p.serial}`}>
      <div className="relative"><img src={p.image_url} alt={p.title} loading="lazy" className="h-44 w-full object-cover" /><StageBadge project={p} className="absolute top-3 left-3" /></div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between text-[10px] font-mono text-[#0d1724]/60"><span>{p.serial}</span><span className="uppercase tracking-widest">{p.category}</span></div>
        <h3 className="font-serif text-xl font-bold text-[#0a0d12] mt-2">{p.title}</h3>
        <EvidenceBadges project={p} className="mt-2" />
        <div className="flex items-center gap-4 text-xs text-[#0a0d12]/60 mt-2">
          {p.location && <span className="flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
          {p.beneficiaries_estimate > 0 && <span className="flex items-center gap-1"><Users size={12} /> {p.beneficiaries_estimate.toLocaleString()} people</span>}
        </div>
        {stage === "under_verification" ? (
          <p className="mt-4 text-[11px] text-[#8a6a1f] bg-[#f8f4e8] border border-[#d6aa43]/30 rounded-md px-3 py-2" data-testid={`urgent-unconfirmed-${p.serial}`}>Budget not yet approved — no project-specific donations until verification is complete.</p>
        ) : (
          <>
            <div className="mt-4 h-3 rounded-full bg-black/5 overflow-hidden ring-1 ring-black/5" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${pct}% funded`}><div className="h-full bg-[#d6aa43]" style={{ width: `${Math.max(pct, 2)}%` }} /></div>
            <dl className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div><dt className="text-[#0a0d12]/60">Budget</dt><dd className="font-bold">{target ? `₦${target.toLocaleString()}` : p.target_amount}</dd></div>
              <div><dt className="text-[#0a0d12]/60">Raised</dt><dd className="font-bold">₦{raised.toLocaleString()}</dd></div>
              <div><dt className="text-[#0a0d12]/60">Funding gap</dt><dd className="font-bold">{gap === null ? "—" : `₦${gap.toLocaleString()}`}</dd></div>
            </dl>
          </>
        )}
        <div className="mt-5 flex gap-2 pt-2 mt-auto">
          <Link to={p.serial ? `/projects/${p.serial}/trace` : "/projects"} className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43] !py-2 !px-4 text-xs flex-1 justify-center">View project</Link>
          <StageAction project={p} className="!py-2 !px-4 text-xs flex-1 justify-center" />
        </div>
      </div>
    </article>
  );
};
